<template>
  <div class="mainbody">
    <!-- 顶部操作栏 -->
    <div style="padding: 16px 32px 16px 0px">
      <span class="title">文件系统</span>
      <div style="float: right">
        <el-button type="primary" size="small" @click="showUploadDialog">上传文件</el-button>
        <el-button type="primary" size="small" @click="showCreateFolderDialog">新建文件夹</el-button>
        <el-button type="success" size="small" @click="batchDownload" :disabled="selectedRows.length === 0">批量下载</el-button>
        <el-button type="warning" size="small" @click="showBatchMoveDialog" :disabled="selectedRows.length === 0">批量移动</el-button>
        <el-button type="danger" size="small" @click="batchDelete" :disabled="selectedRows.length === 0">批量删除</el-button>
      </div>
    </div>

    <!-- 搜索和筛选区域 -->
    <div style="padding: 16px 32px; background: #fafafa; margin-bottom: 16px;">
      <el-form :inline="true" :model="searchForm" class="demo-form-inline">
        <el-form-item label="文件名">
          <el-input
            v-model="searchForm.search"
            placeholder="请输入文件名"
            clearable
            @keyup.enter.native="handleSearch"
          ></el-input>
        </el-form-item>
        <el-form-item label="上传时间">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            @change="handleSearch"
          ></el-date-picker>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 文件列表表格 -->
    <div class="tableInfo">
      <el-table 
        :data="fileList" 
        style="width: 100%" 
        v-loading="loading"
        @selection-change="handleSelectionChange"
      >
      <el-table-column type="selection" width="55" :selectable="isSelectable"></el-table-column>
      <el-table-column prop="name" label="名称" min-width="200">
        <template slot-scope="scope">
          <span :class="['file-icon', scope.row.type === 'folder' ? 'folder' : 'file']">
            <i :class="scope.row.type === 'folder' ? 'el-icon-folder' : 'el-icon-document'"></i>
          </span>
          {{ scope.row.name }}
        </template>
      </el-table-column>
      <el-table-column prop="file_ext" label="类型" width="100">
        <template slot-scope="scope">
          {{ scope.row.file_ext }}
        </template>
      </el-table-column>
      <el-table-column prop="update_time" label="最后更新时间" width="160">
        <template slot-scope="scope">
          {{ scope.row.update_time }}
        </template>
      </el-table-column>
      <el-table-column prop="create_admin_name" label="创建者/上传者" width="120">
        <template slot-scope="scope">
          {{ scope.row.create_admin_name }}
        </template>
      </el-table-column>
      <el-table-column prop="file_size_format" label="文件大小" width="100">
        <template slot-scope="scope">
          {{ scope.row.file_size_format }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200" fixed="right">
        <template slot-scope="scope">
          <!-- 如果是 ".." 文件夹，只显示进入文件夹 -->
          <div v-if="scope.row.name === '..'">
            <el-button
              type="text"
              size="small"
              @click="enterFolder(scope.row)"
            >
              打开
            </el-button>
          </div>
          <!-- 普通文件和文件夹显示所有操作 -->
          <div v-else>
            <el-button
              v-if="scope.row.type === 'folder'"
              type="text"
              size="small"
              @click="enterFolder(scope.row)"
            >
              打开
            </el-button>
            <el-button
              v-if="scope.row.type === 'folder'"
              type="text"
              size="small"
              @click="showRenameDialog(scope.row)"
            >
              重命名
            </el-button>
            <el-button
              v-if="scope.row.type === 'folder'"
              type="text"
              size="small"
              @click="downloadFolder(scope.row)"
            >
              下载
            </el-button>
            <el-button 
              v-if="scope.row.type === 'file'"
              type="text" 
              size="small" 
              @click="downloadFile(scope.row)"
            >
              下载
            </el-button>
            <el-button
              v-if="scope.row.type === 'file'"
              type="text"
              size="small"
              @click="showRenameDialog(scope.row)"
            >
              重命名
            </el-button>
            <el-button type="text" size="small" @click="deleteSingle(scope.row)">
              删除
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页信息（仅显示，不可点击） -->
    <div style="margin-top: 16px; text-align: center; color: #999;">
      共 {{ pagination.recordCount }} 条记录，第 {{ pagination.currentPage }} 页 / 共 {{ pagination.pageCount }} 页
    </div>
    </div>

    <!-- 新建文件夹弹窗 -->
    <el-dialog
      title="新建文件夹"
      :visible.sync="createFolderDialogVisible"
      width="400px"
      :before-close="handleClose"
    >
      <el-form :model="createFolderForm" label-width="80px">
        <el-form-item label="文件夹名" required>
          <el-input
            v-model="createFolderForm.folder_name"
            placeholder="请输入文件夹名称"
            maxlength="50"
            show-word-limit
          ></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="createFolderDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="createFolder">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 文件上传弹窗 -->
    <el-dialog
      title="上传文件"
      :visible.sync="uploadDialogVisible"
      width="600px"
      :before-close="() => uploadDialogVisible = false"
    >
      <el-upload
        ref="upload"
        action="#"
        :auto-upload="false"
        :on-change="handleFileChange"
        :on-remove="(file, fileList) => { uploadFileList = fileList }"
        :limit="100"
        multiple
        drag
      >
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">
          将文件拖到此处，或<em>点击上传</em>
        </div>
        <div class="el-upload__tip" slot="tip">
          支持批量选择文件，多个文件将依次上传
        </div>
      </el-upload>
      
      <!-- 已选择的文件列表 -->
      <div v-if="uploadFileList.length > 0" style="margin-top: 16px; max-height: 300px; overflow-y: auto;">
        <el-table :data="uploadFileList" style="width: 100%" size="small">
          <el-table-column prop="name" label="文件名" min-width="200"></el-table-column>
          <el-table-column label="大小" width="100">
            <template slot-scope="scope">
              {{ (scope.row.size / 1024).toFixed(2) }} KB
            </template>
          </el-table-column>
          <el-table-column label="状态/结果" width="150">
            <template slot-scope="scope">
              <!-- 上传中 -->
              <el-tag v-if="uploadingFiles[scope.row.uid]" type="warning" size="mini">上传中...</el-tag>
              <!-- 已上传成功 -->
              <el-tag v-else-if="isFileUploaded(scope.row.name)" type="success" size="mini">✓ 已上传</el-tag>
              <!-- 上传失败，显示失败原因 -->
              <template v-else-if="getUploadResult(scope.row.name)">
                <span v-for="result in [getUploadResult(scope.row.name)]" :key="result.name">
                  <el-tag type="danger" size="mini" effect="plain">✗ {{ result.message }}</el-tag>
                </span>
              </template>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="80">
            <template slot-scope="scope">
              <el-button
                v-if="!uploadingFiles[scope.row.uid] && !isFileUploaded(scope.row.name)"
                type="text"
                size="small"
                @click="removeFile(scope.$index)"
              >
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      
      <!-- 上传结果列表 -->
      <div v-if="uploadResults.length > 0" style="margin-top: 16px; max-height: 200px; overflow-y: auto;">
        <div v-for="(result, index) in uploadResults" :key="index" style="padding: 8px; margin-bottom: 8px; border-radius: 4px; background: #f5f5f5;">
          <i :class="result.status === 'success' ? 'el-icon-circle-check' : 'el-icon-circle-close'" :style="result.status === 'success' ? 'color: #67C23A' : 'color: #F56C6C'"></i>
          <span :style="result.status === 'success' ? 'color: #67C23A' : 'color: #F56C6C'">
            {{ result.name }} - {{ result.message }}
          </span>
        </div>
      </div>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="closeUploadDialog">关 闭</el-button>
        <el-button type="primary" @click="startUpload">开始上传</el-button>
      </span>
    </el-dialog>

    <!-- 批量移动文件夹弹窗 -->
    <el-dialog
      title="批量移动到文件夹"
      :visible.sync="moveDialogVisible"
      width="500px"
    >
      <div v-loading="treeLoading">
        <el-tree
          ref="folderTree"
          :data="folderTreeData"
          :props="{ children: 'children', label: 'folder_name' }"
          node-key="id"
          highlight-current
          default-expand-all
          @node-click="handleNodeClick"
        >
          <span class="custom-tree-node" slot-scope="{ node, data }">
            <span><i class="el-icon-folder" style="color: #f7d046; margin-right: 8px;"></i>{{ node.label }}</span>
          </span>
        </el-tree>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="moveDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="executeBatchMove" :disabled="selectedTargetFolderId === ''">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 重命名文件夹弹窗 -->
    <el-dialog
      :title="renameDialogTitle"
      :visible.sync="renameDialogVisible"
      width="400px"
    >
      <el-form :model="renameForm" label-width="80px">
        <el-form-item label="名称" required>
          <el-input
            v-model="renameForm.new_name"
            :placeholder="'请输入新' + renameForm.type_name"
            maxlength="50"
            show-word-limit
          ></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="renameDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="executeRename">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { URL_PATH } from '@/config/config.js'

export default {
  name: 'AssociationFileSystem',
  data() {
    return {
      loading: false,
      fileList: [],
      searchForm: {
        search: '',
        screen_start_date: '',
        screen_end_date: ''
      },
      dateRange: [],
      pagination: {
        recordCount: 0,
        pageCount: 0,
        currentPage: 1
      },
      currentFolderId: '',
      associationId: this.$route.query.id || '',
      // 新建文件夹弹窗相关
      createFolderDialogVisible: false,
      createFolderForm: {
        folder_name: ''
      },
      // 文件上传弹窗相关
      uploadDialogVisible: false,
      uploadFileList: [],
      uploadProgress: {}, // 每个文件的上传进度
      uploadingFiles: {}, // 正在上传的文件状态
      uploadResults: [], // 上传结果列表
      uploadedFileNames: [], // 已上传成功的文件名（防止重复上传）
      selectedRows: [], // 选中的行（用于批量删除）
      // 批量移动相关
      moveDialogVisible: false, // 移动弹窗
      treeLoading: false, // 树加载状态
      folderTreeData: [], // 文件夹树数据
      selectedTargetFolderId: '', // 选中的目标文件夹 ID
      // 重命名相关
      renameDialogVisible: false, // 重命名弹窗
      renameForm: {
        id: '', // 当前要重命名的 ID（文件或文件夹）
        type: '', // 'file' 或 'folder'
        type_name: '', // '文件名' 或 '文件夹名'
        new_name: '' // 新名称
      }
    }
  },
  created() {
    this.getFileList()
  },
  computed: {
    // 重命名弹窗标题
    renameDialogTitle() {
      return this.renameForm.type === 'folder' ? '重命名文件夹' : '重命名文件'
    }
  },
  methods: {
    // 获取文件列表
    getFileList() {
      this.loading = true
      const params = {
        token: localStorage.getItem('token'),
        association_id: this.associationId,
        folder_id: this.currentFolderId,
        page: 1,
        limit: 100,
        order: 'upload_time,DESC'
      }

      // 添加搜索参数
      if (this.searchForm.search) {
        params.search = this.searchForm.search
      }
      if (this.searchForm.screen_start_date) {
        params.screen_start_date = this.searchForm.screen_start_date
      }
      if (this.searchForm.screen_end_date) {
        params.screen_end_date = this.searchForm.screen_end_date
      }

      this.$api
        .adminAppletsAssociationFileList(params)
        .then(({ data }) => {
          this.fileList = data.Data.model || []
          this.pagination.recordCount = data.Data.recordCount
          this.pagination.pageCount = data.Data.pageCount
          this.pagination.currentPage = data.Data.currentPage
        })
        .catch(({ error }) => {
          console.error('获取文件列表失败:', error)
        })
        .finally(() => {
          this.loading = false
        })
    },

    // 搜索
    handleSearch() {
      if (this.dateRange && this.dateRange.length === 2) {
        this.searchForm.screen_start_date = this.dateRange[0]
        this.searchForm.screen_end_date = this.dateRange[1]
      } else {
        this.searchForm.screen_start_date = ''
        this.searchForm.screen_end_date = ''
      }
      this.getFileList()
    },

    // 重置搜索
    resetSearch() {
      this.searchForm = {
        search: '',
        screen_start_date: '',
        screen_end_date: ''
      }
      this.dateRange = []
      this.getFileList()
    },

    // 进入文件夹
    enterFolder(folder) {
      // 清除搜索参数
      this.searchForm.search = ''
      this.searchForm.screen_start_date = ''
      this.searchForm.screen_end_date = ''
      this.dateRange = []
      
      // 设置 folder_id，接口会自动处理（包括返回上级）
      this.currentFolderId = folder.id
      this.getFileList()
    },

    // 下载文件
    downloadFile(file) {
      if (file.type !== 'file') {
        this.$message.warning('只有文件才能下载')
        return
      }

      // 创建下载链接，直接访问接口
      const token = localStorage.getItem('token')
      const url = `${URL_PATH}api/adminAppletsAssociationFile/download?token=${token}&association_id=${this.associationId}&file_id=${file.id}`
      
      // 创建临时 a 标签触发下载
      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    // 下载文件夹
    downloadFolder(folder) {
      if (folder.type !== 'folder') {
        this.$message.warning('只能下载文件夹')
        return
      }

      // 使用批量下载接口下载单个文件夹
      const token = localStorage.getItem('token')
      
      // 创建临时表单
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = `${URL_PATH}api/adminAppletsAssociationFile/downloadBatch`
      form.style.display = 'none'
      
      // 添加表单字段
      const addField = (name, value) => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = name
        input.value = value
        form.appendChild(input)
      }
      
      addField('token', token)
      addField('association_id', this.associationId)
      addField('folder_ids[]', folder.id)
      
      document.body.appendChild(form)
      form.submit()
      document.body.removeChild(form)
    },

    // 批量下载
    batchDownload() {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请选择要下载的文件或文件夹')
        return
      }

      // 过滤掉 ".." 文件夹
      const validRows = this.selectedRows.filter(row => row.name !== '..')
      
      if (validRows.length === 0) {
        this.$message.warning('不能下载上级文件夹')
        return
      }

      const fileIds = validRows.filter(row => row.type === 'file').map(row => row.id)
      const folderIds = validRows.filter(row => row.type === 'folder').map(row => row.id)

      if (fileIds.length === 0 && folderIds.length === 0) {
        this.$message.warning('请至少选择一个文件或文件夹')
        return
      }

      // 使用表单提交方式下载（POST + 二进制流）
      const token = localStorage.getItem('token')
      
      // 创建临时表单
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = `${URL_PATH}api/adminAppletsAssociationFile/downloadBatch`
      form.style.display = 'none'
      
      // 添加表单字段
      const addField = (name, value) => {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = name
        input.value = value
        form.appendChild(input)
      }
      
      addField('token', token)
      addField('association_id', this.associationId)
      
      // 数组格式提交
      if (fileIds.length > 0) {
        fileIds.forEach(id => addField('file_ids[]', id))
      }
      if (folderIds.length > 0) {
        folderIds.forEach(id => addField('folder_ids[]', id))
      }
      
      document.body.appendChild(form)
      form.submit()
      document.body.removeChild(form)
    },

    // 处理选中项变化
    handleSelectionChange(selection) {
      this.selectedRows = selection
    },

    // 判断行是否可选
    isSelectable(row) {
      // ".." 文件夹不显示复选框
      return row.name !== '..'
    },

    // 单个删除
    deleteSingle(row) {
      const fileIds = row.type === 'file' ? [row.id] : []
      const folderIds = row.type === 'folder' ? [row.id] : []
      
      this.confirmDelete(fileIds, folderIds)
    },

    // 批量删除
    batchDelete() {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请选择要删除的文件或文件夹')
        return
      }

      // 双重保险：过滤掉 ".." 文件夹（即使 selectable 失效）
      const validRows = this.selectedRows.filter(row => row.name !== '..')
      
      if (validRows.length === 0) {
        this.$message.warning('不能删除上级文件夹')
        return
      }

      const fileIds = validRows.filter(row => row.type === 'file').map(row => row.id)
      const folderIds = validRows.filter(row => row.type === 'folder').map(row => row.id)

      this.confirmDelete(fileIds, folderIds)
    },

    // 确认删除对话框
    confirmDelete(fileIds, folderIds) {
      const fileCount = fileIds.length
      const folderCount = folderIds.length
      
      let message = '确定要删除'
      if (fileCount > 0) {
        message += ` ${fileCount} 个文件`
      }
      if (folderCount > 0) {
        if (fileCount > 0) {
          message += '和'
        }
        message += ` ${folderCount} 个文件夹`
      }
      message += '吗？删除后无法恢复！'

      this.$confirm(message, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.executeDelete(fileIds, folderIds)
      }).catch(() => {})
    },

    // 执行删除
    executeDelete(fileIds, folderIds) {
      const params = {
        token: localStorage.getItem('token'),
        association_id: this.associationId,
        file_ids: fileIds,
        folder_ids: folderIds
      }

      this.$api
        .adminAppletsAssociationFileDelete(params)
        .then(({ data }) => {
          const msg = data.Msg || '删除成功'
          this.$message.success(msg)
          this.getFileList()
        })
        .catch(({ error }) => {
          console.error('删除失败:', error)
        })
    },

    // 显示新建文件夹弹窗
    showCreateFolderDialog() {
      this.createFolderForm.folder_name = ''
      this.createFolderDialogVisible = true
    },

    // 创建文件夹
    createFolder() {
      if (!this.createFolderForm.folder_name) {
        this.$message.warning('请输入文件夹名称')
        return
      }

      const params = {
        token: localStorage.getItem('token'),
        association_id: this.associationId,
        folder_name: this.createFolderForm.folder_name,
        parent_id: this.currentFolderId || '0'
      }

      this.$api
        .adminAppletsAssociationFileCreateFolder(params)
        .then(({ data }) => {
          this.$message.success('创建成功')
          this.createFolderDialogVisible = false
          this.getFileList()
        })
        .catch(({ error }) => {
          console.error('创建文件夹失败:', error)
        })
    },

    // 关闭弹窗前的处理
    handleClose() {
      this.createFolderDialogVisible = false
      this.createFolderForm.folder_name = ''
    },

    // 显示上传弹窗
    showUploadDialog() {
      // 每次打开都清空上一次的文件列表和状态
      this.uploadFileList = []
      this.uploadProgress = {}
      this.uploadingFiles = {}
      this.uploadResults = []
      this.uploadedFileNames = []
      
      // 清空 el-upload 组件的文件列表
      if (this.$refs.upload) {
        this.$refs.upload.clearFiles()
      }
      
      this.uploadDialogVisible = true
    },

    // 关闭上传弹窗并刷新列表
    closeUploadDialog() {
      this.uploadDialogVisible = false
      // 关闭时刷新文件列表
      this.getFileList()
    },

    // 显示批量移动弹窗
    showBatchMoveDialog() {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请选择要移动的文件或文件夹')
        return
      }

      // 过滤掉 ".." 文件夹
      const validRows = this.selectedRows.filter(row => row.name !== '..')
      if (validRows.length === 0) {
        this.$message.warning('不能移动上级文件夹')
        return
      }

      this.moveDialogVisible = true
      this.selectedTargetFolderId = ''
      this.getFolderTree(validRows)
    },

    // 获取可移动的文件夹树
    getFolderTree(selectedRows) {
      this.treeLoading = true
      
      // 提取要移动的文件夹 ID 列表（排除文件）
      const folderIds = selectedRows
        .filter(row => row.type === 'folder')
        .map(row => row.id)

      this.$api.adminAppletsAssociationFileGetMovableFolderTree({
        token: localStorage.getItem('token'),
        association_id: this.associationId,
        exclude_folder_ids: folderIds
      }).then(({ data }) => {
        if (data.Code === 0) {
          this.folderTreeData = [data.Data].filter(item => item) // 确保有数据
        } else {
          this.$message.error(data.Msg || '获取文件夹树失败')
          this.folderTreeData = []
        }
      }).catch(err => {
        console.error('获取文件夹树失败:', err)
        this.$message.error('获取文件夹树失败')
        this.folderTreeData = []
      }).finally(() => {
        this.treeLoading = false
      })
    },

    // 点击树节点
    handleNodeClick(data) {
      // 允许选择根目录（id 为 0）或其他文件夹
      this.selectedTargetFolderId = data.id
    },

    // 执行批量移动
    executeBatchMove() {
      if (this.selectedTargetFolderId === '') {
        this.$message.warning('请选择目标文件夹')
        return
      }

      // 过滤掉 ".." 文件夹
      const validRows = this.selectedRows.filter(row => row.name !== '..')
      
      const fileIds = validRows.filter(row => row.type === 'file').map(row => row.id)
      const folderIds = validRows.filter(row => row.type === 'folder').map(row => row.id)

      if (fileIds.length === 0 && folderIds.length === 0) {
        this.$message.warning('请至少选择一个文件或文件夹')
        return
      }

      this.$api.adminAppletsAssociationFileMove({
        token: localStorage.getItem('token'),
        association_id: this.associationId,
        file_ids: fileIds,
        folder_ids: folderIds,
        target_folder_id: this.selectedTargetFolderId
      }).then(({ data }) => {
        if (data.Code === 0) {
          this.$message.success(data.Msg || '移动成功')
          this.moveDialogVisible = false
          this.getFileList() // 刷新列表
          this.selectedRows = [] // 清空选中项
        } else {
          this.$message.error(data.Msg || '移动失败')
        }
      }).catch(err => {
        console.error('移动失败:', err)
        this.$message.error('移动失败')
      })
    },

    // 显示重命名弹窗
    showRenameDialog(row) {
      this.renameForm = {
        id: row.id,
        type: row.type,
        type_name: row.type === 'folder' ? '文件夹名' : '文件名',
        new_name: row.name
      }
      this.renameDialogVisible = true
    },

    // 执行重命名
    executeRename() {
      if (!this.renameForm.new_name) {
        this.$message.warning('请输入名称')
        return
      }

      const params = {
        token: localStorage.getItem('token'),
        association_id: this.associationId
      }

      // 根据类型调用不同的接口
      if (this.renameForm.type === 'folder') {
        params.folder_id = this.renameForm.id
        params.new_folder_name = this.renameForm.new_name
        this.$api.adminAppletsAssociationFileRenameFolder(params)
          .then(({ data }) => {
            if (data.Code === 0) {
              this.$message.success(data.Msg || '重命名成功')
              this.renameDialogVisible = false
              this.getFileList()
            } else {
              this.$message.error(data.Msg || '重命名失败')
            }
          })
          .catch(err => {
            console.error('重命名失败:', err)
            this.$message.error('重命名失败')
          })
      } else if (this.renameForm.type === 'file') {
        params.file_id = this.renameForm.id
        params.new_file_name = this.renameForm.new_name
        this.$api.adminAppletsAssociationFileRename(params)
          .then(({ data }) => {
            if (data.Code === 0) {
              this.$message.success(data.Msg || '重命名成功')
              this.renameDialogVisible = false
              this.getFileList()
            } else {
              this.$message.error(data.Msg || '重命名失败')
            }
          })
          .catch(err => {
            console.error('重命名失败:', err)
            this.$message.error('重命名失败')
          })
      }
    },

    // 处理文件变化
    handleFileChange(file, fileList) {
      this.uploadFileList = fileList
    },

    // 移除文件
    removeFile(index) {
      this.uploadFileList.splice(index, 1)
    },

    // 重置上传结果
    resetUploadResults() {
      this.uploadResults = []
    },

    // 检查文件是否已上传成功
    isFileUploaded(fileName) {
      return this.uploadedFileNames.includes(fileName)
    },

    // 获取文件的上传结果
    getUploadResult(fileName) {
      return this.uploadResults.find(r => r.name === fileName)
    },

    // 开始上传（串行）
    async startUpload() {
      if (this.uploadFileList.length === 0) {
        this.$message.warning('请选择要上传的文件')
        return
      }

      // 过滤掉已经上传成功的文件，只上传未上传和上传失败的
      const filesToUpload = this.uploadFileList.filter(file => !this.isFileUploaded(file.name))
      
      if (filesToUpload.length === 0) {
        this.$message.info('所有文件都已上传成功')
        return
      }

      this.resetUploadResults()
      const total = filesToUpload.length

      // 串行上传，一个一个来
      for (let i = 0; i < total; i++) {
        const file = filesToUpload[i]
        
        // 标记当前正在上传
        this.$set(this.uploadingFiles, file.uid, true)
        
        try {
          const result = await this.uploadSingleFile(file)
          
          // 上传成功
          this.uploadResults.push({
            name: file.name,
            status: 'success',
            message: '上传成功'
          })
          
          // 记录已上传的文件名
          this.uploadedFileNames.push(file.name)
        } catch (error) {
          // 上传失败，从 error 中提取 Msg
          let errorMsg = '上传失败'
          
          // 尝试从不同位置获取错误信息
          if (error.response && error.response.data && error.response.data.Msg) {
            errorMsg = error.response.data.Msg
          } else if (error.message) {
            errorMsg = error.message
          }
          
          // 上传失败
          this.uploadResults.push({
            name: file.name,
            status: 'error',
            message: errorMsg
          })
        } finally {
          // 移除上传中标记
          this.$delete(this.uploadingFiles, file.uid)
        }
      }

      // 所有文件上传完成
      const successCount = this.uploadResults.filter(r => r.status === 'success').length
      const failCount = this.uploadResults.filter(r => r.status === 'error').length
      
      if (failCount === 0) {
        this.$message.success(`全部上传成功，共 ${successCount} 个文件`)
        // 不自动关闭弹窗，让用户自己点击关闭
      } else {
        this.$message.warning(`上传完成，成功 ${successCount} 个，失败 ${failCount} 个`)
      }
    },

    // 上传单个文件
    uploadSingleFile(file) {
      return new Promise((resolve, reject) => {
        const formData = new FormData()
        formData.append('token', localStorage.getItem('token'))
        formData.append('association_id', this.associationId)
        formData.append('folder_id', this.currentFolderId || '0')
        // ThinkPHP6 需要使用 'file' 作为参数名
        formData.append('file', file.raw)
        
        // 使用项目配置的 axios 实例，传入完整的 URL 路径
        const axiosBase = require('axios')
        const { URL_PATH } = require('@/config/config.js')
        const axios = axiosBase.create({
          baseURL: URL_PATH,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        
        axios.post('api/adminAppletsAssociationFile/upload', formData)
        .then(response => {
          const data = response.data
          
          // 检查 Code 是否为 0
          if (data && data.Code === 0) {
            resolve(data)
          } else {
            // Code 不为 0，使用 Msg 作为错误信息
            const errorMsg = (data && data.Msg) ? data.Msg : '上传失败'
            reject(new Error(errorMsg))
          }
        })
        .catch(error => {
          // 如果响应中有数据，尝试提取错误信息
          if (error.response) {
            // 尝试从响应中提取错误信息
            let errorMsg = '上传失败'
            if (error.response.data) {
              // 如果是 JSON 格式
              if (error.response.data.Msg) {
                errorMsg = error.response.data.Msg
              } else if (error.response.data.Message) {
                errorMsg = error.response.data.Message
              } else if (typeof error.response.data === 'string') {
                // 如果是字符串（可能是 HTML 或纯文本错误）
                errorMsg = error.response.data.substring(0, 100)
              }
            }
            reject(new Error(errorMsg))
          } else if (error.message) {
            // 没有 response，但有 error.message（可能是网络错误或请求被拦截）
            reject(new Error(error.message))
          } else {
            // 其他未知错误
            reject(new Error('上传失败'))
          }
        })
      })
    }
  }
}
</script>

<style lang="less" scoped>
.mainbody {
  background-color: #ffffff;
  border-radius: 5px;
  padding-bottom: 58px;
}
.mainbody .title {
  font-family: PingFang SC;
  font-style: normal;
  font-weight: 800;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 0, 0, 0.85);
  padding: 16px 32px;
}
.mainbody .aLine {
  background-color: #e9e9e9;
  height: 1px;
}
.mainbody .tableInfo {
  box-sizing: border-box;
  margin-left: 32px;
  margin-right: 32px;
}

.file-icon {
  display: inline-block;
  margin-right: 8px;
  font-size: 16px;
  
  &.folder {
    color: #f7d046;
  }
  
  &.file {
    color: #409EFF;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/association/associationFileSystem.vue